import { Component, HostListener } from '@angular/core';
import { AsyncPipe, DecimalPipe, Location, NgClass, NgFor, NgIf } from '@angular/common';
import { Router } from '@angular/router';
import { catchError, firstValueFrom, Observable, of, shareReplay } from 'rxjs';
import { DesktopHeaderComponent } from '../../chat-gpt/components/desktop-header/desktop-header.component';
import { DesktopFooterComponent } from '../../chat-gpt/components/desktop-footer/desktop-footer.component';
import { BcraReport, BcraReportStatus, BcraStatusContent, FaqItem, SCREENING_FACTORS } from './bcra-report.model';
import { downloadBcraReportPdf } from './bcra-report-pdf';
import { UserService } from '../../../../../chat-common/src/lib/services/user.service';
import { BcraReportService } from './bcra-report.service';
import { AuthService } from '../../../../../chat-common/src/lib/services/auth.service';
import { environment } from '../../../environments/environment';

// Figma: IVY-HA, node 40:10387 (ready), 40:12755 (FAQ), 216:13821 (not started),
// 216:13230 (incomplete survey), 214:11924 (report pending), 82:34142 (mobile)
@Component({
  selector: 'app-bcra-report',
  standalone: true,
  imports: [NgIf, NgFor, NgClass, AsyncPipe, DecimalPipe, DesktopHeaderComponent, DesktopFooterComponent],
  templateUrl: './bcra-report.component.html'
})
export class BcraReportComponent {
  readonly statusContent: Record<BcraReportStatus, BcraStatusContent | null> = {
    'not-started': {
      title: 'Breast cancer risk assessment',
      body: 'Answer questions about your health and family history to receive a personalized estimate of your breast cancer risk. Your report can help you have a more informed conversation about screening and prevention with a healthcare professional. Once you submit the assessment, your report will be available here within 48 hours.',
      showReviewSurvey: false,
      showReportIssue: false,
      cta: { label: 'Start assessment', quizPath: '' },
    },
    'incomplete': {
      title: 'Your assessment is incomplete',
      body: 'You haven’t completed all the required questions yet. Continue your assessment and submit your answers to generate your personalized report. Once submitted, your report will be available here within 48 hours.',
      showReviewSurvey: false,
      showReportIssue: true,
      cta: { label: 'Continue assessment', quizPath: 'main-menu' },
    },
    'pending': {
      title: 'Your report is being prepared',
      body: 'We’re processing your answers and preparing your personalized Breast Cancer Risk Assessment report. It will be available here within 48 hours. You can return to this page at any time to check its status.',
      showReviewSurvey: true,
      showReportIssue: true,
      cta: null,
    },
    'ready': null,
    'error': {
      title: 'Something went wrong',
      body: 'We couldn’t load your report. Please refresh the page or try again later.',
      showReviewSurvey: false,
      showReportIssue: true,
      cta: null,
    },
  };

  readonly report$: Observable<BcraReport> = this.reportService.getReport().pipe(
    catchError((err) => {
      console.error('Failed to load BCRA report', err);
      return of<BcraReport>({ status: 'error', result: null });
    }),
    // Read by both the mobile top bar and the page body
    shareReplay(1)
  );

  readonly appVersion: string = environment.appVersion;
  readonly supportUrl = 'https://support.ivyhealth.ai/';
  /** Survey review lives on the quiz's main menu. */
  readonly reviewSurveyPath = 'main-menu';
  openingQuiz = false;

  readonly chartMax = 50;
  readonly chartTicks = [0, 10, 20, 30, 40, 50];
  /** Lifetime risk of 20% or more is the usual high-risk threshold, emphasised on the chart. */
  readonly highlightedTick = 20;

  readonly screeningFactors = SCREENING_FACTORS;

  readonly faq: FaqItem[] = [
    {
      question: 'What is the Gail model?',
      answer: [
        { type: 'text', text: 'The Gail model estimates the chance of developing invasive breast cancer over the next five years and up to age 90. This report shows your lifetime estimate.' },
        { type: 'text', text: 'It uses your age, race/ethnicity, age at first period and first live birth, breast biopsy history, and breast cancer in your mother, sisters or daughters.' },
      ],
    },
    {
      question: 'What is breast density, and why does it matter?',
      answer: [
        { type: 'text', text: 'Breast density describes the amount of fibrous and glandular tissue compared with fatty tissue in your breasts. It is assessed on a mammogram.' },
        { type: 'text', text: 'Dense tissue can increase breast cancer risk and make cancer harder to see. Gail does not include breast density, so discuss it separately with your healthcare professional.' },
      ],
    },
    {
      question: 'Why is it helpful to know my risk?',
      answer: [
        { type: 'text', text: 'Understanding your risk can help you have a more informed conversation with your healthcare professional. Depending on your result and other factors, they may recommend a particular screening schedule, additional assessment or genetic counselling.' },
        { type: 'text', text: 'Your risk estimate is only one part of the picture and should not be used to make medical decisions on its own.' },
      ],
    },
    {
      question: 'Does my Gail result determine which screening I need?',
      answer: [
        { type: 'text', text: 'No. Your Gail estimate alone does not determine whether you need MRI or a particular screening schedule. MRI guidance uses other risk factors and models with more detailed family history.' },
        { type: 'text', text: 'Your healthcare professional can review your complete history and help you choose appropriate screening.' },
      ],
    },
    {
      question: 'Are there other breast cancer risk models?',
      answer: [
        { type: 'text', text: 'Yes. Tyrer–Cuzick (IBIS), BCSC and CanRisk use different information and serve different purposes. IVY uses Gail for this assessment.' },
        { type: 'text', text: 'A healthcare professional can advise whether another model would better reflect your history.' },
      ],
    },
    {
      question: 'Can my risk estimate change?',
      answer: [
        { type: 'text', text: 'Yes. Your Gail estimate can change as you age or when relevant information changes, such as a new breast biopsy or breast cancer in a first-degree relative.' },
        { type: 'text', text: 'Update your answers when needed. Changes in breast density or genetic test results may also affect your care, even though Gail does not include them in the calculation.' },
      ],
    },
    {
      question: 'Does a higher risk estimate mean I will develop breast cancer?',
      answer: [
        { type: 'text', text: 'No. A higher estimate does not mean you have breast cancer or will develop it. Gail estimates risk for people with similar factors; it cannot predict what will happen to you.' },
        { type: 'text', text: 'Discuss your result with your healthcare professional, even if it is below average.' },
      ],
    },
    {
      question: 'Can I do anything to reduce my risk?',
      answer: [
        { type: 'text', text: 'Not every risk factor can be changed, but certain habits may help reduce breast cancer risk. These include staying physically active, maintaining a healthy weight and limiting alcohol.' },
        { type: 'text', text: 'For people with a substantially increased risk, a healthcare professional may also discuss risk-reducing medication, genetic counselling or other preventive options. These choices depend on your complete medical history and should always be discussed with a qualified professional.' },
      ],
    },
    {
      question: 'Can I use Gail if I have already had breast cancer?',
      answer: [
        { type: 'text', text: 'Gail is not suitable if you have had invasive breast cancer, DCIS or LCIS. It does not estimate recurrence risk and is also not suitable for people with a known BRCA1/2 mutation.' },
        { type: 'text', text: 'Your healthcare professional can recommend an assessment and follow-up plan that fits your history.' },
      ],
    },
    {
      question: 'Where can I learn more?',
      answer: [
        { type: 'text', text: 'You can find reliable information about breast cancer risk and screening from:' },
        { type: 'link', text: 'American Cancer Society', href: 'https://www.cancer.org/cancer/types/breast-cancer.html' },
        { type: 'link', text: 'National Cancer Institute', href: 'https://www.cancer.gov/types/breast' },
        { type: 'link', text: 'U.S. Preventive Services Task Force', href: 'https://www.uspreventiveservicestaskforce.org/uspstf/recommendation/breast-cancer-screening' },
        { type: 'text', text: 'Screening recommendations may differ between countries and organizations, so discuss your individual result with a healthcare professional.' },
      ],
    },
  ];

  openFaq = new Set<number>();
  downloading = false;

  constructor(
    private reportService: BcraReportService,
    private router: Router,
    private location: Location,
    private authService: AuthService,
    private userService: UserService,
  ) {}

  chartPosition(value: number): number {
    return Math.min(value, this.chartMax) / this.chartMax * 100;
  }

  toggleFaq(index: number) {
    if (this.openFaq.has(index)) {
      this.openFaq.delete(index);
    } else {
      this.openFaq.add(index);
    }
  }

  goBack() {
    this.location.back();
  }

  async download() {
    const report = await firstValueFrom(this.report$);
    if (!report.result || this.downloading) return;
    this.downloading = true;
    try {
      await downloadBcraReportPdf(report.result, {
        userName: this.userService.user.username ?? '',
        generatedAt: new Date(),
      });
    } catch (err) {
      console.error('Failed to generate BCRA report PDF', err);
    } finally {
      this.downloading = false;
    }
  }

  /** Opens the breast health quiz signed in as the current user, via a fresh `#token` access code. */
  async openQuiz(path: string) {
    if (this.openingQuiz) return;
    this.openingQuiz = true;
    const url = new URL(path, environment.breastHealthQuizUrl + '/');
    try {
      const response = await firstValueFrom(this.authService.getAccessToken());
      const accessCode = response.headers.get('x-access-code');
      if (accessCode) {
        url.hash = 'token=' + encodeURIComponent(accessCode);
      }
    } catch (err) {
      // Still open the quiz; the user can sign in there
      console.error('Failed to create an access code for the quiz', err);
    }
    window.location.href = url.toString();
  }

  /** Coming back from the quiz via the back button can restore this page with buttons still disabled. */
  @HostListener('window:pageshow')
  onPageShow() {
    this.openingQuiz = false;
  }

  logout() {
    this.authService.signOut();
  }

  onHeaderSectionSelect() {
    this.router.navigate(['/health-assistant']);
  }
}
